import * as LayoutCreatorEntities from '@bimser/components/dist/BCLayoutCreator/common/entities';
import { FormItemTypes, IDictionary, isNullOrUndefined, MLHelper } from '@bimser/core';
import { Map } from 'immutable';
import _isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import { IControlProperties } from '../../../entities';
import { IDataSource, IVariableItem } from '../../../entities/common';
import { IControlDataGridColumn } from '../../../environments/runtime/components/DataGrid/entities';
import { formulaEvaluate } from '../rule';
import _get from 'lodash/get';

function getLookupActionValue(
	fieldName: string,
	keys: string,
	data: Map<string, LayoutCreatorEntities.LayoutItem>
) {
	const value = data.getIn([fieldName, 'properties', ...keys.split('.')]);
	const selectionMode = data.getIn([fieldName, 'properties', 'gridOptions', 'selection', 'mode']);

	if (selectionMode === 'single') {
		if (keys === 'value') {
			return value && Array.isArray(value) ? value[0] : null;
		}

		if (keys.includes('selectedItem.field.')) {
			const selectedItems: unknown[] = data.getIn(
				[fieldName, 'properties', 'selectedItems'],
				[]
			);
			if (selectedItems?.length) {
				return selectedItems[0][keys.split('.')[2]];
			}
		}

		if (!_isEmpty(value) && Array.isArray(value)) {
			return value[0];
		}
	}

	return value;
}

function getFormActionValue(
	fieldName: string,
	keys: string,
	data: Map<string, LayoutCreatorEntities.LayoutItem>
) {
	if (keys.indexOf('variables.') === 0) {
		const variable: IVariableItem = data.getIn([fieldName, 'properties', ...keys.split('.')]);
		if (variable) {
			return formulaEvaluate(variable.value, data);
		}
	}
}

function getNumberBoxActionValue(
	fieldName: string,
	keys: string,
	data: Map<string, LayoutCreatorEntities.LayoutItem>
) {
	if (keys === 'text') {
		const value: number = data.getIn([fieldName, 'properties', 'value']);
		if (isNullOrUndefined(value)) return '';
		return value.toString();
	}
}

function getAutoCompleteActionValue(
	fieldName: string,
	keys: string,
	data: Map<string, LayoutCreatorEntities.LayoutItem>
) {
	if (keys === 'value' || keys === 'selectedItem.value') {
		const items: any[] = data.getIn([fieldName, 'properties', 'items']);
		const selectedItem = items?.find(i => i.selected);

		return selectedItem?.value;
	}

	if (keys === 'text' || keys === 'selectedItem.text') {
		const items: any[] = data.getIn([fieldName, 'properties', 'items']);
		const selectedItem = items?.find(i => i.selected);

		if (selectedItem) {
			return MLHelper.getMLText(selectedItem.text) || '';
		}
	}

	if (keys.includes('selectedItem.field.')) {
		const rawData: any[] = data.getIn([fieldName, 'properties', 'data']);
		const items: any[] = data.getIn([fieldName, 'properties', 'items']);
		const dataSource: IDataSource = data.getIn([fieldName, 'properties', 'dataSource']);
		const selectedItem = items?.find(i => i.selected);

		if (selectedItem && rawData) {
			const row = rawData.find(i => i[dataSource.valueExpr] === selectedItem.value);
			const selectedField = keys.split('.')[2];

			return row[selectedField];
		}
	}
}

function getTransferActionValue(
	fieldName: string,
	keys: string,
	data: Map<string, LayoutCreatorEntities.LayoutItem>,
	ignoreValueSeparator: boolean
) {
	if (keys === 'value' || keys === 'text') {
		const items: any[] = data.getIn([fieldName, 'properties', 'items']);
		const valueSeparator: string = data.getIn([fieldName, 'properties', 'valueSeparator']);
		const selectedItems = items?.filter(i => i.selected);

		if (ignoreValueSeparator) {
			return selectedItems?.map(i => i.value) || [];
		}

		return selectedItems?.length
			? selectedItems.map(i => i.value).join(valueSeparator || ',')
			: null;
	}
	if (keys === 'selectedValues') {
		const items: any[] = data.getIn([fieldName, 'properties', 'items']);
		return items
			?.map(item => {
				if (item.selected) {
					return item.value;
				}
			})
			.filter(i => i);
	}
}

function getTextAreaActionValue(
	fieldName: string,
	keys: string,
	data: Map<string, LayoutCreatorEntities.LayoutItem>
) {
	if (keys === 'value' || keys === 'text') {
		const multiLanguageText: IDictionary<string> = data.getIn([
			fieldName,
			'properties',
			'multiLanguageText'
		]);
		const enableMultiLanguageText: boolean = data.getIn([
			fieldName,
			'properties',
			'enableMultiLanguageText'
		]);

		if (!enableMultiLanguageText) {
			if (multiLanguageText) {
				return multiLanguageText['system'];
			}
			return '';
		}

		return MLHelper.getMLText(multiLanguageText) || '';
	}
}

function getDateTimePickerActionValue(
	fieldName: string,
	keys: string,
	data: Map<string, LayoutCreatorEntities.LayoutItem>
) {
	if (keys === 'value' || keys === 'text') {
		const value = data.getIn([fieldName, 'properties', keys]);
		return value ? dayjs(value).startOf('day') : null;
	}
}

function getDateRangePickerActionValue(
	fieldName: string,
	keys: string,
	data: Map<string, LayoutCreatorEntities.LayoutItem>
) {
	if (keys === 'startDate.value' || keys === 'endDate.value') {
		const value = data.getIn([fieldName, 'properties', ...keys.split('.')]);
		return value ? dayjs(value).startOf('day') : null;
	}
}

function getTimePickerActionValue(
	fieldName: string,
	keys: string,
	data: Map<string, LayoutCreatorEntities.LayoutItem>
) {
	if (keys === 'value' || keys === 'text') {
		const value = data.getIn([fieldName, 'properties', keys]);
		const format = data.getIn([fieldName, 'properties', 'format']);

		if (!value) {
			return null;
		} else if (format === 'HH:mm') {
			return value.set('second', 0);
		}
		return value;
	}
}

function getDataGridActionValue(
	fieldName: string,
	keys: string,
	data: Map<string, LayoutCreatorEntities.LayoutItem>
) {
	if (keys.includes('columns')) {
		const dataGridPath = keys.split('.');
		const columns: Array<IControlDataGridColumn<IControlProperties>> = data.getIn([
			fieldName,
			'properties',
			dataGridPath[0]
		]);
		const column = columns.find(col => col.name === dataGridPath[1]);
		return _get(column, dataGridPath.splice(2).toString().replace(',', '.'));
	} else if (keys.includes('selectedRows.length') || keys.includes('selectedRowsData.length')) {
		return data.getIn([fieldName, 'properties', 'selectedRowsData'])?.length || 0;
	} else if (keys.includes('selectedRowKeys.length')) {
		return data.getIn([fieldName, 'properties', 'selectedRowKeys'])?.length || 0;
	}
}

export function getActionValue(
	fieldName: string,
	keys: string,
	data: Map<string, LayoutCreatorEntities.LayoutItem>,
	args?: { ignoreValueSeparator?: boolean }
) {
	const controlType: FormItemTypes.EControlTypes | FormItemTypes.EContainerTypes = data.getIn([
		fieldName,
		'properties',
		'controlType'
	]);
	let value: any;
	switch (controlType) {
		case FormItemTypes.EControlTypes.Lookup: {
			value = getLookupActionValue(fieldName, keys, data);
			break;
		}
		case FormItemTypes.EContainerTypes.Form: {
			value = getFormActionValue(fieldName, keys, data);
			break;
		}
		case FormItemTypes.EControlTypes.ComboBox:
		case FormItemTypes.EControlTypes.Dropdown:
		case FormItemTypes.EControlTypes.RadioList:
		case FormItemTypes.EControlTypes.TreeView:
		case FormItemTypes.EControlTypes.AutoComplete: {
			value = getAutoCompleteActionValue(fieldName, keys, data);
			break;
		}
		case FormItemTypes.EControlTypes.ListBox:
		case FormItemTypes.EControlTypes.TagBox:
		case FormItemTypes.EControlTypes.Transfer: {
			value = getTransferActionValue(fieldName, keys, data, args?.ignoreValueSeparator);
			break;
		}
		case FormItemTypes.EControlTypes.TextBox:
		case FormItemTypes.EControlTypes.ProtectedTextBox:
		case FormItemTypes.EControlTypes.TextArea: {
			value = getTextAreaActionValue(fieldName, keys, data);
			break;
		}
		case FormItemTypes.EControlTypes.DateTimePicker: {
			value = getDateTimePickerActionValue(fieldName, keys, data);
			break;
		}
		case FormItemTypes.EControlTypes.DateRangePicker: {
			value = getDateRangePickerActionValue(fieldName, keys, data);
			break;
		}
		case FormItemTypes.EControlTypes.TimePicker: {
			value = getTimePickerActionValue(fieldName, keys, data);
			break;
		}
		case FormItemTypes.EControlTypes.DataGrid: {
			value = getDataGridActionValue(fieldName, keys, data);
			break;
		}
		case FormItemTypes.EControlTypes.NumberBox: {
			value = getNumberBoxActionValue(fieldName, keys, data);
			break;
		}
	}

	return value === undefined ? data.getIn([fieldName, 'properties', ...keys.split('.')]) : value;
}
